/**
 * HCaptcha Error Messages
 *
 * Centralized location for all error strings used in the hCaptcha implementation.
 * This allows for easy maintenance, consistency, and potential localization in the future.
 *
 * @module src/captcha/hcaptcha/errors
 */

/**
 * Error messages for execution failures
 */
export const EXECUTION_ERRORS = {
  /** Thrown when hCaptcha SDK is not initialized before execution */
  NOT_INITIALIZED: 'hCaptcha is not initialized',

  /** Thrown when widget ID is not available for token generation */
  WIDGET_ID_MISSING: 'hCaptcha widget ID is not available',

  /** Thrown when execute() returns no response token */
  NO_RESPONSE_RECEIVED: 'No response received from hCaptcha',
} as const;

/**
 * Error messages for rendering failures
 */
export const RENDER_ERRORS = {
  /** Thrown when hCaptcha SDK is not available during rendering */
  SDK_NOT_AVAILABLE: 'hCaptcha SDK not available for rendering',

  /** Thrown when render() returns an invalid widget ID */
  INVALID_WIDGET_ID: 'Failed to render hCaptcha widget - render returned invalid ID',
} as const;

/**
 * Error messages for callback handling
 */
export const CALLBACK_ERRORS = {
  /** Message when token expires (30 minute validity) */
  TOKEN_EXPIRED: 'Token expired',

  /** Message when challenge expires */
  CHALLENGE_EXPIRED: 'Challenge expired',
} as const;

/**
 * Error context messages for logging
 */
export const ERROR_CONTEXT = {
  /** Context for errors in execute method */
  EXECUTE: 'execute',

  /** Context for errors in render method */
  RENDER: 'render',

  /** Context for errors in callback handling */
  CALLBACK: 'callback',

  /** Context for errors in error reporting */
  ERROR_REPORTING: 'reportErrorEvent',

  /** Context for errors in event reporting */
  EVENT_REPORTING: 'reportEventData',

  /** Context for errors in token hashing */
  TOKEN_HASHING: 'hashToken',

  /** Context for errors in container creation */
  CONTAINER_CREATION: 'createContainer',
} as const;

/**
 * Get all error messages organized by category
 *
 * Useful for error analysis, logging, and testing
 */
export const ALL_ERRORS = {
  execution: EXECUTION_ERRORS,
  rendering: RENDER_ERRORS,
  callbacks: CALLBACK_ERRORS,
} as const;

/**
 * Type-safe error message type
 *
 * Ensures only valid error messages are used
 */
export type HCaptchaErrorMessage =
  | (typeof EXECUTION_ERRORS)[keyof typeof EXECUTION_ERRORS]
  | (typeof RENDER_ERRORS)[keyof typeof RENDER_ERRORS]
  | (typeof CALLBACK_ERRORS)[keyof typeof CALLBACK_ERRORS];

/**
 * Type-safe error context type
 *
 * Ensures only valid error contexts are used
 */
export type HCaptchaErrorContext = (typeof ERROR_CONTEXT)[keyof typeof ERROR_CONTEXT];
